import React, {useEffect, useState} from 'react';
import {Skeleton} from '@material-ui/lab';
import {reduxToggleSidePanel} from 'appredux/actions';
import {WalletRestrictionForm} from 'components/WalletRestrictionForm/WalletRestrictionForm';

import {
  apiCreateAccountRestriction,
  apiGetWalletRestrictionList,
} from 'constants/Api';
import {IWalletRestriction} from 'constants/interfaces';

interface AddOrUpdateWalletRestrictionsProps {
  walletId?: string | number;
  walletBalance?: number;
  existingRestrictions?: Array<{
    restictionAmount: number;
    subCategory?: {id: number};
    categories?: {id: number};
  }>;
  restriction?: {
    category: string;
    maxSpend: number;
  };

  // NEW: callback that the parent (WalletScreen) can pass in to show a toast
  onRestrictionAdded?: () => void;
}

export default function AddOrUpdateWalletRestrictions(
  props: AddOrUpdateWalletRestrictionsProps,
) {
  const isAdding = !props.restriction;

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);

  // Store full subcategory objects here
  const [subCategories, setSubCategories] = useState<IWalletRestriction[]>([]);

  // Fetch real subcategories from the API
  useEffect(() => {
    setIsLoadingCategories(true);
    apiGetWalletRestrictionList()
      .then((fetchedSubs) => {
        // fetchedSubs is an array of { id, code, description }
        setSubCategories(fetchedSubs);
      })
      .catch((err) => {
        console.error('Error fetching categories:', err);
        setSubCategories([]);
      })
      .finally(() => setIsLoadingCategories(false));
  }, []);

  // Called by the form after user clicks "Save"
  const handleSubmitRestriction = async (submission: {
    category: string;
    maxSpend: number;
  }) => {
    if (!props.walletId) {
      alert('No walletId provided – cannot create or edit restriction!');
      return;
    }

    setIsLoading(true);
    try {
      // Ensure maxSpend is numeric (in case it's a string)
      const numericAmount = parseFloat(String(submission.maxSpend));

      if (isAdding) {
        // Adding scenario
        const response = await apiCreateAccountRestriction(
          String(props.walletId),
          submission.category, // This is the subcat ID
          numericAmount,
        );
        if (!response?.success && response !== undefined) {
          alert('Failed to create restriction');
        } else {
          // NEW: Instead of a success alert, call parent’s callback
          props.onRestrictionAdded?.();
        }
      } else {
        // Editing scenario — same upsert endpoint handles both
        const response = await apiCreateAccountRestriction(
          String(props.walletId),
          submission.category,
          numericAmount,
        );
        if (!response?.success && response !== undefined) {
          alert('Failed to update restriction');
        } else {
          props.onRestrictionAdded?.();
        }
      }

      // close side panel
      reduxToggleSidePanel(false, null);
    } catch (err) {
      console.error('Error creating/editing restriction:', err);
      alert('Error creating/editing restriction. See console for details.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingCategories) {
    return (
      <div style={{padding: 16}}>
        <Skeleton variant="rect" height={80} />
      </div>
    );
  }

  return (
    <WalletRestrictionForm
      walletBalance={props.walletBalance}
      existingRestrictions={props.existingRestrictions}
      restriction={props.restriction}
      subCategories={subCategories}
      isLoading={isLoading}
      onSubmit={handleSubmitRestriction}
      onCancel={() => reduxToggleSidePanel(false, null)}
    />
  );
}
